import { useState } from 'react';

import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import InputBase from '@mui/material/InputBase';
import { alpha } from '@mui/material/styles';

import IconButton from 'components/@extended/IconButton';
import DrawerHeader from 'components/ui/DrawerHeader';
import { metallicPalette } from 'themes/colors';
import { HEADER_HEIGHT } from '../../index';

import CustomerServiceOutlined from '@ant-design/icons/CustomerServiceOutlined';
import SearchOutlined from '@ant-design/icons/SearchOutlined';
import BookOutlined from '@ant-design/icons/BookOutlined';
import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined';
import MailOutlined from '@ant-design/icons/MailOutlined';
import PhoneOutlined from '@ant-design/icons/PhoneOutlined';
import FileTextOutlined from '@ant-design/icons/FileTextOutlined';
import SendOutlined from '@ant-design/icons/SendOutlined';

export default function HelpSupport() {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketMsg, setTicketMsg] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const helpTopics = [
    { title: 'Employee Onboarding & Profiles', desc: 'Add new staff, upload documents, and manage roles', category: 'HR Basics' },
    { title: 'Attendance & Punch Regularization', desc: 'Approve missing punches and view shift logs', category: 'Attendance' },
    { title: 'Leave Policy & Approvals', desc: 'Request leaves, view balances, and approve workflow', category: 'Leaves' },
    { title: 'Payroll Calculation & Payslips', desc: 'Generate monthly payroll, tax, and payslip downloads', category: 'Payroll' }
  ];

  const filteredTopics = helpTopics.filter(
    (t) =>
      t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.desc.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  function handleSubmitTicket(e) {
    e.preventDefault();
    if (!ticketSubject || !ticketMsg) return;
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setTicketSubject('');
      setTicketMsg('');
    }, 3000);
  }

  return (
    <Box sx={{ flexShrink: 0 }}>
      {/* Top Header Headset Icon Button */}
      <Tooltip title="Help & Support">
        <IconButton
          color="secondary"
          onClick={() => setOpen(true)}
          sx={{
            width: 30,
            height: 30,
            borderRadius: '8px',
            color: metallicPalette.grey[7],
            bgcolor: open ? alpha(metallicPalette.primary.main, 0.1) : 'transparent',
            '&:hover': {
              bgcolor: alpha(metallicPalette.primary.main, 0.08),
              color: metallicPalette.primary.main
            }
          }}
        >
          <CustomerServiceOutlined style={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>

      {/* ── Slide-Out Help & Support Right Panel ── */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 5,
          '& .MuiBackdrop-root': {
            top: `${HEADER_HEIGHT}px`
          },
          '& .MuiDrawer-paper': {
            top: `${HEADER_HEIGHT}px !important`,
            height: `calc(100vh - ${HEADER_HEIGHT}px) !important`,
            maxHeight: `calc(100vh - ${HEADER_HEIGHT}px) !important`,
            width: { xs: '100vw', sm: 380 },
            bgcolor: '#FFFFFF',
            boxShadow: '-4px 10px 30px rgba(0, 0, 0, 0.12)',
            display: 'flex',
            flexDirection: 'column',
            m: '0 !important',
            p: '0 !important'
          }
        }}
      >
        {/* Slim Reusable Global Drawer Header (Single Line 42px) */}
        <DrawerHeader title="Help & Support" onClose={() => setOpen(false)} />

        {/* Scrollable Content Body */}
        <Box className="slim-scroll" sx={{ flex: 1, p: 2, overflowY: 'auto' }}>
          {/* Quick Search */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: metallicPalette.grey[1],
              border: `1px solid ${metallicPalette.grey[3]}`,
              borderRadius: '8px',
              px: 1.5,
              py: 0.6,
              mb: 2,
              '&:focus-within': {
                borderColor: metallicPalette.primary.main,
                bgcolor: '#FFFFFF'
              }
            }}
          >
            <SearchOutlined style={{ color: metallicPalette.grey[5], fontSize: 14, marginRight: 8 }} />
            <InputBase
              placeholder="Search help articles & guides..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: '100%', fontSize: 12, fontWeight: 500, color: metallicPalette.grey[9] }}
            />
          </Box>

          {/* Contact Support Direct Card */}
          <Box
            sx={{
              p: 1.8,
              borderRadius: '10px',
              bgcolor: alpha(metallicPalette.primary.main, 0.05),
              border: `1px solid ${alpha(metallicPalette.primary.main, 0.15)}`,
              mb: 2
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  bgcolor: metallicPalette.primary.main,
                  color: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  justify: 'center'
                }}
              >
                <CustomerServiceOutlined style={{ fontSize: 16 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: metallicPalette.grey[9], lineHeight: 1.2 }}>
                  Live HR Support Desk
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: metallicPalette.grey[6], mt: 0.2 }}>
                  Available Mon - Sat (9:00 AM - 6:00 PM)
                </Typography>
              </Box>
            </Stack>

            <Stack spacing={0.8} sx={{ pt: 0.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <MailOutlined style={{ fontSize: 12, color: metallicPalette.primary.main }} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: metallicPalette.grey[8] }}>
                  support@workora.com
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <PhoneOutlined style={{ fontSize: 12, color: metallicPalette.primary.main }} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: metallicPalette.grey[8] }}>
                  +91 1800-WORKORA
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {/* Knowledge Base Articles */}
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              color: metallicPalette.grey[5],
              textTransform: 'uppercase',
              px: 0.5,
              mb: 1,
              letterSpacing: 0.5
            }}
          >
            Popular Help Guides
          </Typography>

          <List disablePadding sx={{ mb: 2 }}>
            {filteredTopics.map((topic) => (
              <ListItemButton
                key={topic.title}
                sx={{
                  py: 1.2,
                  px: 1.5,
                  borderRadius: '8px',
                  mb: 0.6,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  bgcolor: metallicPalette.grey[1],
                  border: `1px solid ${metallicPalette.grey[2]}`,
                  '&:hover': {
                    bgcolor: alpha(metallicPalette.primary.main, 0.08),
                    borderColor: alpha(metallicPalette.primary.main, 0.2)
                  }
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    bgcolor: alpha(metallicPalette.primary.main, 0.1),
                    color: metallicPalette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    justify: 'center',
                    flexShrink: 0
                  }}
                >
                  <BookOutlined style={{ fontSize: 15 }} />
                </Box>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: metallicPalette.grey[9], lineHeight: 1.2 }}>
                    {topic.title}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: metallicPalette.grey[5], lineHeight: 1.3, mt: 0.2 }}>
                    {topic.desc}
                  </Typography>
                </Box>
              </ListItemButton>
            ))}
          </List>

          {/* Submit Support Ticket Section */}
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              color: metallicPalette.grey[5],
              textTransform: 'uppercase',
              px: 0.5,
              mb: 1,
              letterSpacing: 0.5
            }}
          >
            Create Support Ticket
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmitTicket}
            sx={{
              p: 1.8,
              borderRadius: '10px',
              bgcolor: metallicPalette.grey[1],
              border: `1px solid ${metallicPalette.grey[3]}`
            }}
          >
            {submitted ? (
              <Box sx={{ textAlign: 'center', py: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#10B981', mb: 0.3 }}>
                  Ticket Submitted Successfully!
                </Typography>
                <Typography sx={{ fontSize: 11, color: metallicPalette.grey[6] }}>
                  Our HR support desk will respond to your registered email shortly.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1.2}>
                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: metallicPalette.grey[8], mb: 0.4 }}>
                    Subject
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: '#FFFFFF',
                      border: `1px solid ${metallicPalette.grey[3]}`,
                      borderRadius: '6px',
                      px: 1.2,
                      py: 0.5
                    }}
                  >
                    <InputBase
                      placeholder="Brief issue title"
                      value={ticketSubject}
                      onChange={(e) => setTicketSubject(e.target.value)}
                      sx={{ width: '100%', fontSize: 11.5, fontWeight: 500 }}
                    />
                  </Box>
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: metallicPalette.grey[8], mb: 0.4 }}>
                    Description
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: '#FFFFFF',
                      border: `1px solid ${metallicPalette.grey[3]}`,
                      borderRadius: '6px',
                      px: 1.2,
                      py: 0.5
                    }}
                  >
                    <InputBase
                      multiline
                      rows={2.5}
                      placeholder="Describe your issue or query..."
                      value={ticketMsg}
                      onChange={(e) => setTicketMsg(e.target.value)}
                      sx={{ width: '100%', fontSize: 11.5, fontWeight: 500 }}
                    />
                  </Box>
                </Box>

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  startIcon={<SendOutlined style={{ fontSize: 12 }} />}
                  sx={{
                    height: 32,
                    borderRadius: '6px',
                    bgcolor: metallicPalette.primary.main,
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: metallicPalette.primary.dark
                    }
                  }}
                >
                  Submit Ticket
                </Button>
              </Stack>
            )}
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}
